import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useLocation, useNavigate } from "react-router-dom";
import { GetOnlinePolicy } from "../../Service/_index";
import PaymentQrCard from "../Policy/OnlinePolicy/components/PaymentQrCard";
import { qrEndsAt, qrState } from "../Policy/OnlinePolicy/utils/paymentQr";
import { onPolicyEvent } from "../../Service/realtime";
import { useQrQueue } from "./qrQueue";

const nameOf = (p) => [p && (p.fullName || p.firstName), p && (p.surname || p.lastName)].filter(Boolean).join(" ").trim();

/** Open the presigned picture through a plain link: S3 links refuse fetch() (CORS) but work as href. */
const openLink = (url, filename) => {
  if (!url) return;
  const a = document.createElement("a");
  a.href = url;
  if (filename) a.download = filename;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * The KSHEMA payment QR as a dialog, on whatever page the creator is on, the moment the automation has it (2026-10-22).
 *
 * The socket only says WHICH policy; the picture is read through the normal single-policy API, so the server's QR rule
 * (5 minutes, not after Complete / Cancelled - backend shared/paymentQr.js) still decides. The countdown and the
 * "expired" state are the View page's own PaymentQrCard. Several QRs wait their turn. Nothing opens when the creator is
 * already on that policy's View page - the card is right there.
 *
 * @param nowFn  clock (tests)
 */
const PaymentQrDialog = ({ nowFn = Date.now }) => {
  const queue = useQrQueue((s) => s.queue);
  const drop = useQrQueue((s) => s.drop);
  const head = queue[0];
  const [policy, setPolicy] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const load = useCallback(
    async (id) => {
      const res = await GetOnlinePolicy(id, { silent: true });
      const p = res && !res.error ? res.data : null;
      if (!p || qrState(p.paymentQrScreenshot, p.status, nowFn()).state !== "live") {
        drop(id);
        setPolicy(null);
        return;
      }
      setPolicy(p);
    },
    [drop, nowFn]
  );

  // The next QR in line.
  useEffect(() => {
    setPolicy(null);
    if (!head) return;
    if (location.pathname.includes(`/view/form/${head.policyId}`)) {
      drop(head.policyId);
      return;
    }
    load(head.policyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [head && head.policyId]);

  // Closes itself when its time is up.
  useEffect(() => {
    if (!policy) return undefined;
    const ends = qrEndsAt(policy.paymentQrScreenshot);
    if (ends === null) return undefined;
    const t = setTimeout(() => drop(String(policy._id)), Math.max(0, ends - nowFn()) + 1500);
    return () => clearTimeout(t);
  }, [policy, drop, nowFn]);

  // The policy changed while shown (paid, completed, a new QR): read it again.
  useEffect(() => {
    if (!head) return undefined;
    return onPolicyEvent((e) => {
      if (e.type === "changed" && e.policyId === head.policyId) load(head.policyId);
    });
  }, [head, load]);

  if (!head || !policy) return null;
  const id = String(policy._id || head.policyId);
  const doc = policy.paymentQrScreenshot;
  const close = () => drop(id);

  return (
    <Dialog open onClose={close} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }} data-testid="payment-qr-dialog">
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Typography component="div" sx={{ fontWeight: 700, fontSize: 18, color: "#101828" }}>
          Payment QR ready
        </Typography>
        <Typography component="div" sx={{ fontSize: 13, color: "#667085", mt: 0.25 }}>
          {[nameOf(policy), policy.company].filter(Boolean).join(" - ")}
        </Typography>
        {queue.length > 1 && <Chip size="small" label={`${queue.length - 1} more waiting`} sx={{ mt: 1 }} />}
        <IconButton aria-label="Close" onClick={close} sx={{ position: "absolute", right: 10, top: 10 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Box sx={{ pt: 0.5 }}>
          <PaymentQrCard
            doc={doc}
            policyStatus={policy.status}
            nowFn={nowFn}
            onView={() => openLink(doc && doc.presignedUrl)}
            onDownload={() => openLink(doc && doc.presignedUrl, (doc && doc.fileName) || "Payment_QR.png")}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} color="inherit">
          Close
        </Button>
        <Button
          variant="contained"
          endIcon={<OpenInNewIcon />}
          onClick={() => {
            close();
            navigate(`/onlinePolicy/view/form/${id}`);
          }}
          sx={{ backgroundColor: "#1E3A5F" }}
        >
          Open policy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentQrDialog;
